import { getPropertiesEditableInComponent } from "@/lib/components/editable-properties";
import { ExportedTree, TreeNode } from "@/types/tree";
import { treeNodeReduce } from "@/lib/tree/tree";
import {
  ChangeControlValueMeta,
  Control,
  ControlsTreeContext
} from "@/types/controls";
import { configStore, documentStore, uiStore } from "@/store/store";
import { Target } from "@/types/components";
import {
  getControlAtPath2,
  removeConditionsFromControls
} from "@/lib/components/util";
import lodash from "lodash";
import { addMetadataToDocumentPropertyChangeBasingOnControlValueChangeMetadata } from "@/lib/controls/utils";
import {
  getElementControlsListBasedOnPathPrefix,
  separatePathPrefixFromElementControlPath
} from "@/lib/elements/utils";
import {
  getElementAccordionState,
  setElementAccordionState
} from "@/lib/panels/accordions";

export function makeTargetsFromComponent(
  treeForComponent: ExportedTree
): Target[] {
  return treeNodeReduce(
    (node: TreeNode, targets: Target[]) => {
      return [
        ...targets,
        ...getPropertiesEditableInComponent(node).map(editableProperty => {
          return {
            nodeId: node.id,
            controlPath: editableProperty.controlPath,
            propertyKey: editableProperty.propertyKey
          };
        })
      ];
    },
    treeForComponent.root,
    []
  );
}

export function getValuesFromComponent(targetNode: TreeNode) {
  const baseValues =
    lodash.get(
      targetNode.data.properties,
      "content.content.block.properties"
    ) || {};

  return treeNodeReduce(
    (node, acc) => {
      const editableProps = getPropertiesEditableInComponent(node);
      const values = editableProps.reduce((propAcc, prop) => {
        propAcc[prop.controlPath] = baseValues[prop.propertyKey];
        return propAcc;
      }, {} as Record<string, any>);

      acc[node.id] = values;
      return acc;
    },
    targetNode,
    {} as Record<string, any>
  );
}

export function makeControlsFromComponent(
  treeForComponent: ExportedTree
): Control[] {
  return removeConditionsFromControls(
    treeNodeReduce(
      (node: TreeNode, acc: Control[]) => {
        const controls: Control[] = [];

        getPropertiesEditableInComponent(node)
          .filter(property => {
            return "enabled" in property ? property.enabled : true;
          })
          .forEach(editableProperty => {
            const control = getControlAtPath2(
              editableProperty.controlPath,
              configStore.element(node.data.type).controls
            );
            if (control) {
              controls.push({
                ...control,
                label: editableProperty.label
                  ? editableProperty.label
                  : control.label,
                slug: editableProperty.propertyKey
              });
            }
          });

        return [...acc, ...controls];
      },
      treeForComponent.root,
      []
    )
  );
}

export const editingElementControlsTreeContext: ControlsTreeContext = {
  getControlValue: controlPath => {
    return lodash.get(uiStore.activeElement, `data.properties.${controlPath}`);
  },
  setControlValue(
    controlPathProbablyWithBreakpointIdAndHoverSuffix,
    newValue,
    control,
    meta?: ChangeControlValueMeta
  ) {
    if (!uiStore.activeElementId) return;

    documentStore.throttledPropertyChanged(
      addMetadataToDocumentPropertyChangeBasingOnControlValueChangeMetadata(
        {
          elementId: uiStore.activeElementId,
          path: controlPathProbablyWithBreakpointIdAndHoverSuffix,
          value: newValue
        },
        {
          control,
          controlsTree:
            this.getControls(
              controlPathProbablyWithBreakpointIdAndHoverSuffix
            ) || []
        },
        meta
      )
    );
  },
  getControls: (controlPath: string): Control[] | undefined => {
    if (!uiStore.activeElement) {
      return undefined;
    }

    const splittedPath = separatePathPrefixFromElementControlPath(controlPath);

    if (splittedPath) {
      return getElementControlsListBasedOnPathPrefix(
        configStore.element(uiStore.activeElement.data.type),
        splittedPath.prefix
      );
    }

    return undefined;
  },
  setAccordionState: (accordionPath: string, open: boolean) => {
    if (!uiStore.activeElementId) return;
    setElementAccordionState(uiStore.activeElementId, accordionPath, open);
  },
  getAccordionState: (accordionPath: string) => {
    if (!uiStore.activeElementId) return false;
    return getElementAccordionState(uiStore.activeElementId, accordionPath);
  }
};
